import { Button, Panel } from "@appkit4/react-components";
import { createSelector } from "@reduxjs/toolkit";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import banner from "../../assets/banner.png";
import toast from "../../components/DismissibleToast";
import RequestAccessModal from "../../components/RequestAccessModal";
import SectionLoader from "../../components/sectionLoader/SectionLoader";
import { AuthContext } from "../../services/AuthProvider";
import {
  getGroupData,
  getGroupID,
  setGroupData,
} from "../../services/GroupContext";
import sqlService from "../../services/sqldatabase/sqldatabase.service";
import {
  setIsClientFacing,
  setIsTenantAdmin,
  setTenantID,
  setTooltips,
  setUserGroupRole,
} from "../../store/slice/usageInfoSlice";

import AdminPanel from "./components/AdminPanel";
import GroupSelector from "./components/GroupSelector";
import RequestAccess from "./components/RequestAccess";
import TenantSelector from "./components/TenantSelector";
import "./home.scss";

const Welcome = ({ setSupportIconVisible }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [groupId, setGroupId] = useState("");
  const [tenantList, setTenantList] = useState([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedGroupName, setSelectedGroupName] = useState(null);
  const [requestGroup, setRequestGroup] = useState("");
  const [userGroupList, setUserGroupList] = useState([]);
  const [otherGroupList, setOtherGroupList] = useState([]);
  const [requestAccessModal, setRequestAccessModal] = useState(false);
  const [nonReadonlyButtonTooltip, setNonReadonlyButtonTooltip] = useState("");
  const navigate = useNavigate();
  const navToNew = () => navigate("/app/new");
  const auth = useContext(AuthContext);
  const dispatch = useDispatch();

  const [isFetchingGroups, setIsFetchingGroups] = useState(false);

  const [isReadOnlyUser, setIsReadOnlyUser] = useState(auth?.isReadOnlyUser());
  const [lockedGroup, setLockedGroup] = useState("");
  const [isGroupLocked, setIsGroupLocked] = useState(false);

  const [adminPanelVisible, setAdminPanelVisible] = useState(false);

  const selectUsageInfo = createSelector(
    (state) => state.usageInfo,
    (usageInfo) => ({
      isSystemAdmin: usageInfo.isSystemAdmin,
      isClientFacing: usageInfo.isClientFacing,
      isTenantAdmin: usageInfo.isTenantAdmin,
    }),
  );

  const { isSystemAdmin, isClientFacing, isTenantAdmin } =
    useSelector(selectUsageInfo);

  const showAdminPanel =
    selectedTenant &&
    !isLoading &&
    ((isSystemAdmin && !isClientFacing) || isTenantAdmin);
  const dropdownsDisabled = !selectedTenant;

  const fetchTenants = async () => {
    try {
      const response = await sqlService?.getTenantList();
      if (response?.err || response?.status !== 200) {
        throw new Error(
          response?.err?.message ?? response?.err ?? "Failed to fetch tenants",
        );
      }
      const tenants = response?.data?.sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      setTenantList(tenants);

      if (tenants?.length === 1) {
        const tenantId = tenants[0].id;
        fetchGroups(tenantId, tenants);
        setSelectedTenant(tenantId);
      }
    } catch (error) {
      toast.error(`Failed to download tenant list: ${error?.message}`, {
        duration: 30 * 1000,
      });
    } finally {
      setIsLoadingTenants(false);
    }
  };

  const fetchGroups = async (tenantId, passedTenants = tenantList) => {
    if (isFetchingGroups) return;

    setIsFetchingGroups(true);
    setIsLoading(true);

    const isClientFacing = !passedTenants?.find(
      (tenant) => tenant.id === tenantId,
    )?.isInternal;
    dispatch(setIsClientFacing(isClientFacing));
    setSupportIconVisible(true);

    try {
      const response = await sqlService?.getTenantData(tenantId);
      if (response.err) {
        const message =
          response.status === 403
            ? `User doesn't have access to this tenant`
            : (response.message ?? "Error Fetching Tenant Data");
        throw new Error(message);
      }
      const isTenantAdmin = response?.data?.isTenantAdmin || false;
      dispatch(setIsTenantAdmin(isTenantAdmin));
      const { userGroups, otherGroups, tooltips } = response?.data;
      setUserGroupList(userGroups);
      setOtherGroupList(otherGroups);
      dispatch(setTooltips(tooltips || {}));
      setIsLoading(false);
    } catch (error) {
      setSelectedTenant("");
      setIsLoading(false);
      toast.error(`Failed to download group list: ${error?.message || error}`, {
        duration: 8 * 1000,
      });
    } finally {
      setIsFetchingGroups(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    setGroupId("");
    setSelectedGroupName("");
    dispatch(setTenantID(selectedTenant));
    if (selectedTenant) {
      fetchGroups(selectedTenant);
    } else {
      setSupportIconVisible(false);
      setUserGroupList([]);
    }
  }, [selectedTenant]);

  useEffect(() => {
    const isReadonly = auth?.isReadOnlyUser();
    setIsReadOnlyUser(isReadonly);

    if (isReadonly) {
      setNonReadonlyButtonTooltip("You do not have access to edit this group");
    }

    if (groupId) {
      fetchGroupData(groupId, isReadonly);
    }
  }, [groupId]);

  // submit function run on opening a group
  const fetchGroupData = async (groupId, readOnly = false) => {
    setIsLoading(true);

    let tmpGroupLock;

    const promise = sqlService?.getGroupData(selectedTenant, groupId, readOnly);
    return promise
      .then(({ data }) => {
        setIsLoading(false);
        if (!readOnly) setLockedGroup(groupId);
        setIsGroupLocked(data?.readonly?.value);
        tmpGroupLock = data?.readonly;
        setGroupData(data?.group_data);
        dispatch(setUserGroupRole(data?.userGroupRole));
      })
      .then(() => {
        if (tmpGroupLock?.value) {
          toast(
            `This group is currently being edited by ${tmpGroupLock?.user}`,
            { icon: "⚠️" },
          );

          if (!readOnly) {
            setNonReadonlyButtonTooltip(
              `This group is currently being edited by ${tmpGroupLock.user}.`,
            );
            return Promise.reject("Group is locked");
          }
        }

        return Promise.resolve();
      })
      .catch((reason) => {
        setIsLoading(false);
        setGroupId(null);

        const lockMessage =
          typeof reason?.err === "string" ? reason.err : reason?.err?.message;
        const message =
          (typeof reason === "string"
            ? reason
            : (lockMessage ?? reason?.message)) ||
          "Unknown Error fetching group";
        toast.notification(`Cannot lock group: ${message}`, {
          icon: "⚠️",
        });
        console.error(reason);
      });
  };

  const openSelectedGroup = async (readOnly = false) => {
    if (readOnly) {
      auth?.setReadonly(readOnly);
    }

    const groupData = getGroupData();

    if (!isGroupLocked && !readOnly) {
      const lockGroup = sqlService.postLockGroup(selectedTenant, getGroupID());
      lockGroup
        .then((response) => {
          if (response.status !== 200) {
            throw response;
          }

          navigate(
            groupData["status_groupconfig_completed"]
              ? "app/grouppoa"
              : "/app/groupconfig",
          );
        })
        .catch((err) => {
          toast.notification(
            `Cannot lock group: ${err.err ?? err.error ?? err.message ?? ""}`,
            { icon: "⚠️" },
          );
        });
    } else {
      navigate(
        groupData["status_groupconfig_completed"]
          ? "app/grouppoa"
          : "/app/groupconfig",
      );
    }
  };

  return (
    <div className="ap-container">
      <div className="row my-2">
        <div className="col-6 test">
          <h1 className="title m-3">
            Hello {auth?.getFirstName()}, <br></br>Welcome to the Corporate
            Interest Restriction Analyser!
          </h1>
          <p style={{ color: "white" }} className="m-3">
            Select or request access to an existing group from the drop down
            menus below, or create a new group.
          </p>
        </div>
        <div className="col-6 test2">
          <img src={banner} alt="banner" className="banner" />
        </div>
      </div>
      <Panel className="mt-4 mb-4 custom-padding-top">
        <p>
          The “Select an existing group” dropdown menu allows the user to select
          any group for which they have been given access. Once the relevant
          group is selected, the user can select “Edit group” to edit
          information in that group, or “Open read-only” to view, but not edit,
          information for that group.
        </p>
        <p>
          The “Request access to an existing group” dropdown menu allows the
          user to select any group for which they wish to be granted access.
          Once the relevant group is selected, the user can select “Request
          access”.
        </p>
        <p>
          Where a group does not currently exist, the user can create a new
          group by clicking on the “Create new group” button, which will take
          the user to the Group setup page.
        </p>
      </Panel>
      {isLoadingTenants ? (
        <SectionLoader />
      ) : tenantList?.length > 0 ? (
        <Panel className="custom-padding-top">
          {tenantList?.length > 1 && (
            <TenantSelector
              tenantList={tenantList}
              setSelectedTenant={setSelectedTenant}
              isLoading={isLoading}
              dropdownsDisabled={dropdownsDisabled}
            />
          )}
          <br />
          {selectedTenant && (
            <GroupSelector
              selectedGroupName={selectedGroupName}
              isLoading={isLoading}
              userGroupList={userGroupList}
              groupId={groupId}
              setSelectedGroupName={setSelectedGroupName}
              setGroupId={setGroupId}
              lockedGroup={lockedGroup}
              isReadOnlyUser={isReadOnlyUser}
              isGroupLocked={isGroupLocked}
              nonReadonlyButtonTooltip={nonReadonlyButtonTooltip}
              openSelectedGroup={openSelectedGroup}
              dropdownsDisabled={dropdownsDisabled}
            />
          )}
          {selectedTenant && !isTenantAdmin && !isLoading && (
            <RequestAccess
              otherGroupList={otherGroupList}
              requestGroup={requestGroup}
              setRequestGroup={setRequestGroup}
              setRequestAccessModal={setRequestAccessModal}
              isLoading={isLoading}
              fetchGroups={fetchGroups}
              dropdownsDisabled={dropdownsDisabled}
            />
          )}
          <RequestAccessModal
            {...{ requestAccessModal, setRequestAccessModal, requestGroup }}
          />
          <div className="flex-center">
            {selectedTenant && !isLoading && (
              <div>
                <Button
                  onClick={navToNew}
                  loading={isLoading}
                  disabled={!selectedTenant}
                >
                  Create new group
                </Button>
              </div>
            )}
            {showAdminPanel && (
              <div>
                <Button
                  onClick={() => setAdminPanelVisible(true)}
                  loading={isLoading}
                >
                  Admin Panel
                </Button>
              </div>
            )}
          </div>

          <AdminPanel
            visible={adminPanelVisible}
            setVisible={setAdminPanelVisible}
            userGroupList={userGroupList}
            fetchGroups={fetchGroups}
            isSystemAdmin={isSystemAdmin}
            isClientFacing={isClientFacing}
          ></AdminPanel>
        </Panel>
      ) : (
        <Panel className="custom-padding-top">
          <h3>You currently don’t have access to any tenant.</h3>
          <h1>
            To get access or resolve this issue, please contact XXXX for tenant
            support.
          </h1>
        </Panel>
      )}
    </div>
  );
};

export default Welcome;
